import { useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Icon, type IconName } from "@/components/ui/Icon";
import { useUpdateWorkspace } from "@/hooks/useUpdateWorkspace";
import { showToast } from "@/lib/toast";
import { t } from "@/lib/strings";
import type { UUID } from "@/lib/types";

const WORKSPACE_ICONS: { name: IconName; label: string }[] = [
  { name: "rauten", label: "LogicLine" },
  { name: "code-xml", label: "IT / Code" },
  { name: "megaphone", label: "Marketing" },
  { name: "box", label: "Technik" },
  { name: "notepad-text", label: "Office" },
  { name: "briefcase", label: "Business" },
  { name: "building", label: "Verwaltung" },
  { name: "chart", label: "Analytics" },
  { name: "headset", label: "Support" },
  { name: "handshake", label: "Außendienst / Vertrieb" },
  { name: "ship-wheel", label: "Management" },
];

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: UUID;
  workspaceName: string;
  currentIcon: string | null;
}

export function WorkspaceIconDialog({
  open,
  onOpenChange,
  workspaceId,
  workspaceName,
  currentIcon,
}: Props) {
  const update = useUpdateWorkspace();
  const [pending, setPending] = useState<string | null>(null);

  async function pick(value: string | null) {
    setPending(value ?? "__none__");
    try {
      await update.mutateAsync({ workspaceId, payload: { icon: value } });
      showToast(t.workspaceIconSaved, "success");
      onOpenChange(false);
    } catch (err) {
      showToast(err instanceof Error ? err.message : t.unknownError, "error");
    } finally {
      setPending(null);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => !update.isPending && onOpenChange(o)}
      title={t.workspaceIconTitle.replace("{name}", workspaceName)}
      description={t.workspaceIconDescription}
      width={420}
    >
      <div
        style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginTop: 4 }}
      >
        {WORKSPACE_ICONS.map((ic) => {
          const selected = currentIcon === ic.name;
          return (
            <button
              key={ic.name}
              type="button"
              title={ic.label}
              aria-label={ic.label}
              aria-pressed={selected}
              disabled={pending !== null}
              onClick={() => pick(ic.name)}
              style={{
                height: 56,
                borderRadius: 8,
                border: `1px solid ${selected ? "var(--ink-1)" : "var(--hairline)"}`,
                outline: selected ? "1px solid var(--ink-1)" : "none",
                background: selected ? "var(--surface-soft)" : "var(--surface)",
                color: "var(--ink-1)",
                cursor: pending !== null ? "wait" : "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "border-color .12s, background .12s, transform .12s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "translateY(-1px)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "translateY(0)")}
            >
              <Icon name={ic.name} size={22} />
            </button>
          );
        })}
      </div>

      <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid var(--hairline-soft)" }}>
        <button
          type="button"
          onClick={() => pick(null)}
          disabled={update.isPending}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            width: "100%",
            padding: "10px 12px",
            background: "transparent",
            border: "1px solid var(--hairline)",
            borderRadius: 8,
            cursor: update.isPending ? "not-allowed" : "pointer",
            fontSize: 13,
            color: "var(--ink-1)",
            fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
            textAlign: "left",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-soft)")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
        >
          <span
            style={{
              width: 22,
              height: 22,
              borderRadius: 6,
              border: "1px dashed rgba(0,0,0,0.25)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 10,
              fontWeight: 700,
              color: "var(--ink-3)",
              flexShrink: 0,
            }}
          >
            Ab
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 500 }}>{t.workspaceIconNone}</div>
            <div style={{ fontSize: 11.5, color: "var(--ink-3)" }}>{t.workspaceIconNoneHint}</div>
          </div>
          {currentIcon === null ? <Icon name="check" size={14} color="var(--ink-2)" /> : null}
        </button>
      </div>
    </Dialog>
  );
}
